import { computed, defineComponent, inject, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import html_iway from "./template.iway.html?raw";
import { currentOrderProviderKey } from "../../../infrastructure/current_order_provider";
import { orderServiceKey } from "../../../infrastructure/gfo_api/order_service";
import type { AccessFiberConfigPatchRequest } from "../../../infrastructure/gfo_api/order_api";
import { ApiValidationError } from "../../../infrastructure/gfo_api/api_validation_error";
import ErrorMessage from "../../Shared/ErrorMessage";

/**
 * GFO-1375 Bei Oto-ID nur unzulässige Zeichen filtern. Keine Auto-Formatierung
 */
const app_theme = import.meta.env.VITE_APP_THEME;
let html = "";
if ("iway" == app_theme) {
  html = html_iway;
} else if ("lic" == app_theme) {
  html = html_iway;
} else if ("sak" == app_theme) {
  html = html_iway;
}
export default defineComponent({
  template: html,
  components: {ErrorMessage},
  emits: {
    next: () => true,
  },
  setup() {
    // Injections
    const { t } = useI18n();
    const orderService = inject(orderServiceKey);
    const currentOrderProvider = inject(currentOrderProviderKey);

    // vars und computed
    const config = computed(
      () =>
        currentOrderProvider?.currentOrder?.value?.configs.find(
          (c) => c.type == "AccessFiberConfig",
        ) ?? null,
    );
    const generalError = ref("");
    const otoIdSource = ref("");
    const otoIdSourceError = ref("");
    const otoId = ref<string | null>(null);
    const otoIdError = ref("");
    const otoIdList = computed(
      () =>
        currentOrderProvider?.currentOrder?.value?.access_context?.oto_ids ??
        [],
    );
    const otoIdSourceChoices = computed(() => {
      let choices = [];
      choices.push({
        key: "not_available",
        text: t("ConfigsAccess_OtoId_NotAvailable"),
      });
      if (0 < otoIdList.value.length) {
        choices.push({
          key: "checker_list",
          text: t("ConfigsAccess_OtoId_CheckerList"),
        });
      }
      choices.push({
        key: "own_entry",
        text: t("ConfigsAccess_OtoId_OwnEntry"),
      });
      return choices;
    });

    //methods
    const onOtoIdSourceChange = async (_evt: Event) => {
      if (otoIdSource.value == "not_available"){
        otoId.value = null;
        await save();
      }
      if (otoIdSource.value == "checker_list"){
        otoId.value = null;
        await save();
      }
    };

    const onOtoIdChange = async (_evt: Event) => {
      await save();
    }

    // laufender Autosave-Request; validate() muss darauf warten, bevor es den
    // (sonst optimistisch geleerten) Fehlerstatus auswertet
    let pendingSave: Promise<void> | null = null;

    const save = async () => {
      const run = async () => {
        otoIdError.value = "";
        otoIdSourceError.value = "";
        generalError.value = "";
        const conf = config.value as any;
        if (conf == null || orderService == null) {
          return;
        }
        const request: AccessFiberConfigPatchRequest = {
          oto_id_source: otoIdSource.value,
          oto_id: otoId.value !== null ? otoId.value.trim() : otoId.value,
        };
        try {
          await orderService.patchConfig(conf["uuid"], conf.type, request);
        } catch(err){
          if (err instanceof ApiValidationError) {
            otoIdError.value = err.errors["oto_id"]?.[0] ?? "";
            otoIdSourceError.value = err.errors["oto_id_source"]?.[0] ?? "";
          } else {
            generalError.value = `${err}`;
          }
        }
      };
      pendingSave = run();
      await pendingSave;
    };

    const isValid = computed(() => {
      if (!otoIdSource.value) {
        return false;
      }
      if (otoIdSource.value === "own_entry" && !otoId.value) {
        return false;
      }
      if (otoIdSource.value === "checker_list" && !otoId.value) {
        return false;
      }
      return !otoIdError.value && !otoIdSourceError.value;
    });

    const validate = async () => {
      if (pendingSave) {
        await pendingSave;
      }
      if (!otoIdSource.value) {
        otoIdSourceError.value = t("Validation_Field_Required");
      }
      if (otoIdSource.value === "own_entry" && !otoId.value) {
        otoIdError.value = t("Validation_Field_Required");
      }
      if (otoIdSource.value === "checker_list" && !otoId.value) {
        otoIdError.value = t("Validation_Field_Required");
      }
      return isValid.value;
    };

    onMounted(function () {
      const conf = config.value as any;
      if (conf == null) {
        return;
      }
      if (!conf?.data?.oto_id_source && 0 < otoIdList.value.length){
        otoIdSource.value = "checker_list";
      } else {
        otoIdSource.value = conf.data?.oto_id_source ?? "";
        otoId.value = conf.data?.oto_id ?? null;
      }
    });

    return {
      t,
      generalError,
      otoId,
      otoIdList,
      otoIdError,
      otoIdSource,
      otoIdSourceError,
      otoIdSourceChoices,
      onOtoIdSourceChange,
      onOtoIdChange,
      save,
      isValid,
      validate,
    };
  },
} as any);
